import { useState } from 'react';
import { ChevronDown, Star, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';

import type { LibraryItem, UserShowStatus } from '@shared/types';

import { ResponsiveDialog } from '@/components/ResponsiveDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useFinishTitle, useSetStatus, useTitleRating } from '@/features/show/api';
import { Stars } from '@/features/show/Stars';
import { STATUS_ORDER, type SelectableStatus } from '@/features/show/tracking';
import { useLocale } from '@/i18n/locale';
import { STATUS_NOUN_KEY } from '@/lib/library';
import { cn } from '@/lib/utils';

import { patchLibraryRow } from './api';

export interface CoverQuickActionsProps {
  item: LibraryItem;
  /** Localized title name — accessible names and the remove confirmation. */
  name: string;
}

/** Compact control on the bar: the ghost button, one step smaller than `sm`. */
const BAR_BUTTON = 'h-8 gap-1 px-2 text-caption text-ink hover:text-ink';

/**
 * The cover wall's hover quick actions (screens.md §`/library`): a bar pinned
 * to the bottom edge of a cover that fades in on hover or focus-within — the
 * grid only renders it on `(hover: hover) and (pointer: fine)`, never on
 * touch. Two controls: the rating (★ → a `Popover` with the roving-tabindex
 * `Stars`, `PUT /v1/shows/:id/rating`) and the status `DropdownMenu`
 * (`PUT /v1/shows/:id/status`; a series marked Completed goes through the
 * same finish flow as the title page; Remove… confirms in a
 * `ResponsiveDialog`). Both write optimistically to the shelf cache
 * (`patchLibraryRow`); failures toast with Retry (the show mutations) and
 * the settle refetch restores the truth.
 *
 * The bar is absolutely positioned (no layout shift) and sits outside the
 * cover link, so clicking it never navigates. While a menu, popover or the
 * dialog is open the bar stays visible (focus has left the tile).
 */
export function CoverQuickActions({ item, name }: CoverQuickActionsProps) {
  const { t } = useLocale();
  const qc = useQueryClient();
  const id = item.title.id;
  const isSeries = item.title.type === 'show';

  const setStatus = useSetStatus(id, item.title.type);
  const finishTitle = useFinishTitle(id, item.title.type);
  const rateTitle = useTitleRating(id, item.title.type);

  const [ratingOpen, setRatingOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pinned = ratingOpen || menuOpen || confirmOpen;

  if (item.status === 'removed') return null;
  const status: SelectableStatus = item.status;
  const rating = item.userRating ?? null;
  const statusLabel = t(STATUS_NOUN_KEY[status]);

  // Films offer Watchlist + Completed (app parity with the title page's
  // status sheet); an out-of-range current status still shows, selected.
  const options = STATUS_ORDER.filter(
    (s) => isSeries || s === 'watchlist' || s === 'completed' || s === status,
  );

  const onRate = (value: number | null) => {
    patchLibraryRow(qc, id, { rating_user: value });
    rateTitle.mutate(value);
  };

  const applyStatus = (value: string) => {
    const next = value as SelectableStatus;
    if (next === status) return;
    patchLibraryRow(qc, id, { user_status: next });
    if (isSeries && next === 'completed') finishTitle.mutate();
    else setStatus.mutate(next);
  };

  const confirmRemove = () => {
    setConfirmOpen(false);
    const removed: UserShowStatus = 'removed';
    patchLibraryRow(qc, id, { user_status: removed });
    setStatus.mutate(removed);
  };

  return (
    <div
      role="group"
      aria-label={t('library.quick.group', { title: name })}
      className={cn(
        'absolute inset-x-1.5 bottom-1.5 z-10 flex items-center justify-between gap-1 rounded bg-surface p-1 transition-opacity duration-fast ease-out',
        pinned
          ? 'opacity-100'
          : 'opacity-0 group-focus-within/tile:opacity-100 group-hover/tile:opacity-100',
      )}
    >
      <Popover open={ratingOpen} onOpenChange={setRatingOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={BAR_BUTTON}
            aria-label={
              rating
                ? t('library.quick.rated', { title: name, rating })
                : t('library.quick.rate', { title: name })
            }
          >
            <Star
              aria-hidden
              className={cn('size-4', rating ? 'fill-rating text-rating' : 'text-ink-secondary')}
              strokeWidth={1.75}
            />
            {rating ? (
              <span className="tabular-nums">{rating}</span>
            ) : (
              <span className="hidden md:inline">{t('library.quick.rateShort')}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="flex w-auto flex-col gap-2 p-4">
          <p className="text-caption text-ink-secondary">{t('show.tracking.ratingLabel')}</p>
          <Stars
            value={rating}
            onRate={onRate}
            aria-label={
              rating
                ? t('library.quick.rated', { title: name, rating })
                : t('library.quick.rate', { title: name })
            }
          />
        </PopoverContent>
      </Popover>

      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(BAR_BUTTON, 'min-w-0')}
            aria-label={t('library.quick.status', { title: name, status: statusLabel })}
          >
            <span className="truncate">{t('library.quick.statusShort')}</span>
            <ChevronDown aria-hidden className="size-3.5 shrink-0 text-ink-tertiary" strokeWidth={1.75} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>{t('library.quick.statusShort')}</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={status} onValueChange={applyStatus}>
            {options.map((s) => (
              <DropdownMenuRadioItem key={s} value={s}>
                {t(STATUS_NOUN_KEY[s])}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-danger" onSelect={() => setConfirmOpen(true)}>
            <Trash2 aria-hidden className="size-4" strokeWidth={1.75} />
            {t('library.quick.remove')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ResponsiveDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('library.quick.removeTitle', { title: name })}
        description={t('library.quick.removeBody')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={confirmRemove}>
              {t('library.quick.removeConfirm')}
            </Button>
          </>
        }
      />
    </div>
  );
}
